export const CHANGELOG_ENTRY_TYPES = ["feature", "improvement", "fix"] as const

export type ChangelogEntryType = (typeof CHANGELOG_ENTRY_TYPES)[number]

export interface ChangelogEntry {
  id: string
  title: string
  description: string
  type: ChangelogEntryType
}

export const CHANGELOG_VERSION = "2026.07.10"

export const CHANGELOG_STORAGE_KEY = "odynx-changelog-last-seen"

export const CHANGELOG_ENTRIES: ChangelogEntry[] = [
  {
    id: "xlsx-preview-button-cyclic-fix",
    title: "XLSX preview button fix",
    description:
      'Clicking "Preview for validation" on the prospects XLSX import no longer errors with a serialization failure. The button handler was inadvertently serializing the click event; it now only sends valid title overrides.',
    type: "fix"
  },
  {
    id: "xlsx-preview-cyclic-fix",
    title: "XLSX import preview reliability",
    description:
      "Prospect XLSX preview and import no longer fail when existing PropertyProspects documents contain circular nested data. Large Alfredo extract uploads (e.g. 2,000+ rows) can be validated and confirmed safely.",
    type: "fix"
  },
  {
    id: "xlsx-prospects-upsert",
    title: "Validated XLSX prospects import",
    description:
      "Upload Alfredo extract spreadsheets, review a create/update/skip/error preview, then confirm import. Supports generated title overrides for rows missing a title.",
    type: "feature"
  },
  {
    id: "hot-deals-feature",
    title: "Hot Deals section",
    description:
      "Autonomous Hot Deals page classifies listings against configurable zone rules (admin-managed). Properties are matched by location, typology, price, and area without toggling the legacy isHotDeal flag.",
    type: "feature"
  },
  {
    id: "hot-deals-search-filters",
    title: "Hot Deals search filters",
    description: "Filter Hot Deals in-page with the same saved-filter UX as property search (district, price, ROE, asset type, and more).",
    type: "feature"
  },
  {
    id: "building-search-explorer",
    title: "Building search and explorer",
    description:
      "Building listings now have dedicated search and explorer views with parity to apartments and houses, including Alfredo listing search and ROE enrichment.",
    type: "feature"
  },
  {
    id: "apartments-search-firestore-fix",
    title: "Apartments search stability",
    description:
      "Fixed Firestore multi-range query errors on apartment search and aligned query ordering with composite indexes for reliable pagination.",
    type: "fix"
  },
  {
    id: "job-toggles-complete-list",
    title: "Complete job toggles list",
    description:
      "Job Toggles page now displays all 12 deployed Vercel cron jobs with their corresponding API paths. All jobs that support runtime toggling are now visible and can be enabled/disabled from the admin interface.",
    type: "improvement"
  },
  {
    id: "alfredo-import-manager-tabs",
    title: "Alfredo CSV Import Manager with location tabs",
    description:
      "The Alfredo CSV Import Manager now organizes files by location (Root, Processed, Backup Pending) in separate tabs. Files in subdirectories can be moved back to root with a single click for easy re-import.",
    type: "feature"
  },
  {
    id: "csv-url-validation",
    title: "Improved CSV URL validation",
    description:
      "CSV import now validates URLs properly, filtering out invalid entries like date strings. Additional URL columns from CSV files are automatically detected and stored for complete data capture.",
    type: "improvement"
  },
  {
    id: "master-reset-enhancements",
    title: "Enhanced master reset script",
    description:
      "Master reset script now deletes PropertyProcessing collection entirely and can move files from processed folder back to root. Provides complete reset capability for fresh CSV imports.",
    type: "improvement"
  },
  {
    id: "fix-firestore-duplicate-stages",
    title: "Fixed property processing pipeline errors",
    description:
      "Resolved Firestore duplicate field errors that were preventing properties from being processed through the pipeline. Property processing now correctly updates stage status without conflicts.",
    type: "fix"
  },
  {
    id: "image-postal-code-extraction",
    title: "Visual postal code extraction",
    description:
      "Property location enrichment now analyzes property images to extract postal codes from street signs, building markers, and address plaques. This provides more accurate location data when descriptions are incomplete.",
    type: "feature"
  },
  {
    id: "geocoding-triangulation",
    title: "Smart location triangulation",
    description:
      "Postal code detection now uses geocoding triangulation from nearby businesses and landmarks mentioned in property descriptions. Multiple reference points are weighted by distance and confidence for higher accuracy.",
    type: "improvement"
  },
  {
    id: "timeline-data-fetching",
    title: "Timeline data in property details",
    description:
      "Property details dialog now fetches and displays timeline data from the latest Alfredo listing updates, providing a complete view of property history and status changes.",
    type: "improvement"
  },
  {
    id: "enhanced-location-enrichment",
    title: "Enhanced location enrichment confidence",
    description:
      "Location enrichment agent now factors in image-based extraction and triangulation results when calculating postal code confidence scores, leading to more reliable location data.",
    type: "improvement"
  },
  {
    id: "team-chat-drawer",
    title: "Team chat drawer",
    description:
      "Open the new chat drawer from the header to DM teammates, track read receipts, and keep conversations flowing without leaving your workspace.",
    type: "feature"
  },
  {
    id: "property-sharing-workflows",
    title: "Share filters and listings",
    description:
      "Property cards and saved filters now include Share actions that prefill chat messages with rich summaries so teammates can reopen the exact context you sent.",
    type: "feature"
  },
  {
    id: "notification-center",
    title: "Notification center",
    description:
      "A unified notification bell surfaces unread chat threads and property alerts in one menu with quick mark-all-read and deep links back into the app.",
    type: "feature"
  },
  {
    id: "manual-renovation-overrides",
    title: "Manual renovation overrides stick",
    description:
      "Renovation costs you enter by hand now cascade through ROI math, saved simulations, and AI evaluations instead of being overwritten by automated estimates.",
    type: "improvement"
  },
  {
    id: "cron-load-balancing",
    title: "Balanced cron cadences",
    description:
      "Data ingestion crons were rebalanced to longer staggered cadences, reducing provider load while keeping imports, transfers, and ROI refreshes predictable.",
    type: "improvement"
  }
]
